import * as React from "react";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { WizardCruisePWAProps } from "./typings";
import { CruiseDestination } from "components/flexComponents/WizardCruisePWA/components/CruiseDestination";
import { observer } from "mobx-react";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { SubmitRow } from "src/components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { Dates } from "src/components/shared/Dates/Dates";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import { NoRehydratationValues, TravelersTriggerTypes } from "src/components/shared/TravelersField/typings";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";
import { Location } from "src/components/shared/Location/Location";
import { CruiseHiddenSearchData } from "./components/CruiseHiddenSearchData";

export const WizardCruisePWAView: React.FunctionComponent<WizardCruisePWAProps> = observer(
  ({ flexComponent, wizardState, context }) => {
    const [startId, setStartId] = React.useState<string | undefined>("earliest-departure-date");
    const [endId, setEndId] = React.useState<string | undefined>("latest-departure-date");

    /**
     * This hook is reached for rehydratation, so if JS is enabled there is no need of keep setting IDs to elements
     * so value is changed to 'undefined' and so it is avoided, if JS is disabled then this hook is never executed
     */
    React.useEffect(() => {
      setStartId(undefined);
      setEndId(undefined);
    }, []);
    const {
      globalWizardState: { config: globalConfig },
      cruiseWizardState,
      cruiseWizardState: { config: cruiseConfig },
    } = wizardState;
    const { visibleLobSubtitle } = cruiseWizardState.config.heading;
    const { defaultDestination, defaultMultipleDestination, halfWizard } = globalConfig || {};

    const displayTravelersInline = halfWizard
      ? true
      : isVariantEnabled(context, Experiment.Alignment_of_Traveler_Input);
    const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 6 };
    const destinationColSpan: UitkLayoutConditionalGridSpan = {
      small: 1,
      medium: halfWizard ? 2 : displayTravelersInline ? 2 : 3,
    };
    const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };
    const datesColSpan: UitkLayoutConditionalGridSpan = {
      small: 1,
      medium: halfWizard ? 3 : displayTravelersInline ? 4 : 6,
    };

    /**
     * Values used if no rehydratation(JS disabled)
     */
    const noRehydratationValues: NoRehydratationValues = {
      numOfTravelers: 2,
      name: "adultCount",
    };

    const subtitleTravelersInlinePadding: UitkSpacingProps["padding"] = { block: "three" };

    return (
      <form
        noValidate={true}
        action={cruiseConfig.form.action}
        autoComplete="off"
        className="WizardCruisePWA"
        id={cruiseConfig.elementId}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        data-testid="wizard"
        onSubmit={cruiseWizardState.submit}
      >
        <LobTitle lobState={cruiseWizardState} />

        <Viewport>
          <ViewSmall>
            <>
              {visibleLobSubtitle && (
                <UitkSpacing padding={subtitleTravelersInlinePadding}>
                  <LobSubtitle lobState={cruiseWizardState} />
                </UitkSpacing>
              )}

              {displayTravelersInline && (
                <UitkSpacing padding={subtitleTravelersInlinePadding}>
                  <Travelers
                    lobState={cruiseWizardState}
                    colSpan={travelersColSpan}
                    noRehydratationValues={noRehydratationValues}
                    triggerType={TravelersTriggerTypes.LINK_BUTTON}
                  />
                </UitkSpacing>
              )}
            </>
          </ViewSmall>
          <ViewMedium>
            <UitkSpacing padding={subtitleTravelersInlinePadding}>
              <UitkLayoutFlex data-testid="travelers-inline-link" alignItems={"center"} justifyContent="space-between">
                {visibleLobSubtitle && (
                  <UitkLayoutFlexItem>
                    <LobSubtitle lobState={cruiseWizardState} />
                  </UitkLayoutFlexItem>
                )}
                {displayTravelersInline && (
                  <UitkLayoutFlexItem>
                    <div>
                      <Travelers
                        lobState={cruiseWizardState}
                        colSpan={travelersColSpan}
                        noRehydratationValues={noRehydratationValues}
                        triggerType={TravelersTriggerTypes.LINK_BUTTON}
                      />
                    </div>
                  </UitkLayoutFlexItem>
                )}
              </UitkLayoutFlex>
            </UitkSpacing>
          </ViewMedium>
        </Viewport>

        <UitkSpacing padding={{ block: "three" }}>
          <UitkLayoutGrid columns={mainGridColumns} space="three">
            <>
              {!defaultDestination && !defaultMultipleDestination && (
                <CruiseDestination lobState={cruiseWizardState} colSpan={destinationColSpan} />
              )}

              {(defaultDestination || defaultMultipleDestination) && (
                <Location
                  originField={false}
                  destinationField={true}
                  lobState={cruiseWizardState}
                  colSpan={travelersColSpan}
                  globalConfig={globalConfig}
                />
              )}

              {!displayTravelersInline && (
                <Travelers
                  lobState={cruiseWizardState}
                  colSpan={travelersColSpan}
                  noRehydratationValues={noRehydratationValues}
                />
              )}
              <Viewport>
                <ViewSmall>
                  <Dates
                    startId={startId}
                    endId={endId}
                    lobState={cruiseWizardState}
                    colSpan={datesColSpan}
                    stackedDates={true}
                  />
                </ViewSmall>
                <ViewMedium>
                  <Dates startId={startId} endId={endId} lobState={cruiseWizardState} colSpan={datesColSpan} />
                </ViewMedium>
              </Viewport>
            </>
          </UitkLayoutGrid>
        </UitkSpacing>
        <CruiseHiddenSearchData cruiseWizardState={cruiseWizardState} />

        <SubmitRow lobState={cruiseWizardState} rfrr={"SearchClick_Cruises"} globalConfig={globalConfig} />
      </form>
    );
  }
);

export default WizardCruisePWAView;
